import { Link } from "react-router-dom";
import { useState } from "react";
import {
  MagnifyingGlass, SignOut, ListChecks, Crown,
  List, X, Gauge, CaretDown,
  ShieldCheck, ShieldWarning, ListChecks as BulkIcon,
  LinkSimple, Stethoscope, MapPin, Buildings,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/AuthContext";

const Header = () => {
  const { user, isAuthenticated, login, signup, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = user?.name
    ? user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0"
            data-testid="header-logo"
            title="NPI Lookup — Verify any US healthcare provider"
            aria-label="NPI Lookup — Home"
          >
            <div
              className="w-7 h-7 bg-navy rounded-lg flex items-center justify-center"
              role="img"
              aria-label="NPI Lookup logo"
            >
              <MagnifyingGlass className="w-4 h-4 text-white" weight="bold" aria-hidden="true" />
            </div>
            <span className="font-display font-bold text-lg text-navy">NPI Lookup</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <NavLink to="/pecos-lookup" label="PECOS Lookup" title="Check Medicare enrollment status" testId="nav-pecoslookup" />
            <NavLink to="/oig-exclusion-lookup" label="OIG Lookup" title="Search federal exclusion database" testId="nav-oigexclusionlookup" />
            <NavLink to="/bulk-screening" label="Bulk Screening" title="CSV bulk PECOS + OIG compliance check" testId="nav-bulkscreening" />
            <NavLink to="/dea-npi-lookup" label="DEA-NPI Lookup" title="Cross-reference DEA and NPI numbers" testId="nav-deanpilookup" />

            {/* Browse Dropdown */}
            <NavDropdown label="Browse" testId="nav-browse">
              <DropdownLink to="/find-a-doctor" icon={<Stethoscope className="w-4 h-4 text-teal" weight="fill" />} label="Find a Doctor" desc="Search by condition or specialty" />
              <DropdownLink to="/specialties" icon={<Buildings className="w-4 h-4 text-navy" weight="fill" />} label="Specialties" desc="Browse all specialties" />
              <DropdownLink to="/states" icon={<MapPin className="w-4 h-4 text-red-500" weight="fill" />} label="Browse by State" desc="Providers in every state" />
            </NavDropdown>

            <NavLink to="/pricing" label="Pricing" title="View subscription plans" testId="nav-pricing" />

            {isAuthenticated && (
              <NavLink to="/dashboard" label="Dashboard" title="Your account dashboard" testId="nav-dashboard" />
            )}
          </nav>

          {/* Auth buttons */}
          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <div className="relative group">
                <button className="flex items-center gap-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-navy text-white flex items-center justify-center text-xs font-bold">
                    {initials}
                  </div>
                </button>
                <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 w-52 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <div className="px-3 py-2.5 border-b border-slate-100">
                    <p className="text-sm font-semibold text-slate-900 truncate">{user?.name || "User"}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                    {user?.tier && (
                      <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-teal/10 text-teal">
                        {user.tier} plan
                      </span>
                    )}
                  </div>
                  <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    <Gauge className="w-4 h-4" /> Dashboard
                  </Link>
                  <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    <ListChecks className="w-4 h-4" /> Watchlist
                  </Link>
                  {user?.tier !== "enterprise" && (
                    <Link to="/pricing" className="flex items-center gap-2 px-3 py-2 text-sm text-teal hover:bg-teal/5">
                      <Crown className="w-4 h-4" /> Upgrade Plan
                    </Link>
                  )}
                  {user?.is_admin && (
                    <Link to="/admin" className="flex items-center gap-2 px-3 py-2 text-sm text-amber-600 hover:bg-amber-50" data-testid="nav-admin">
                      <ShieldCheck className="w-4 h-4" weight="fill" /> Admin Panel
                    </Link>
                  )}
                  <hr className="my-1 border-slate-100" />
                  <button
                    onClick={logout}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                    data-testid="logout-btn"
                  >
                    <SignOut className="w-4 h-4" /> Log Out
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={() => login()}
                  className="hidden sm:inline-flex text-sm font-medium text-slate-600 hover:text-navy transition-colors"
                  data-testid="nav-login"
                >
                  Log In
                </button>
                <button
                  onClick={signup}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold text-white bg-teal rounded-lg hover:bg-teal/90 transition-colors"
                  data-testid="nav-signup"
                >
                  Sign Up Free
                </button>
              </>
            )}

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 text-slate-600 hover:text-navy"
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <List className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg" data-testid="mobile-menu">
          <nav className="max-w-7xl mx-auto px-4 py-3 space-y-1">
            <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Compliance Tools</p>
            <MobileLink to="/pecos-lookup" label="PECOS Lookup" onClick={() => setMobileOpen(false)} />
            <MobileLink to="/oig-exclusion-lookup" label="OIG Exclusion Lookup" onClick={() => setMobileOpen(false)} />
            <MobileLink to="/bulk-screening" label="Bulk Screening" onClick={() => setMobileOpen(false)} />
            <MobileLink to="/dea-npi-lookup" label="DEA-NPI Lookup" onClick={() => setMobileOpen(false)} />

            <p className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Browse</p>
            <MobileLink to="/find-a-doctor" label="Find a Doctor" onClick={() => setMobileOpen(false)} />
            <MobileLink to="/specialties" label="Specialties" onClick={() => setMobileOpen(false)} />
            <MobileLink to="/states" label="Browse by State" onClick={() => setMobileOpen(false)} />

            <hr className="my-2 border-slate-100" />
            <MobileLink to="/pricing" label="Pricing" onClick={() => setMobileOpen(false)} />

            {isAuthenticated ? (
              <>
                <MobileLink to="/dashboard" label="Dashboard" onClick={() => setMobileOpen(false)} className="text-navy" />
                <button onClick={() => { logout(); setMobileOpen(false); }} className="block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50">
                  Log Out
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { login(); setMobileOpen(false); }} className="block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Log In
                </button>
                <button onClick={() => { signup(); setMobileOpen(false); }} className="block w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold text-teal hover:bg-teal/5">
                  Sign Up Free
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

const NavLink = ({ to, label, title, testId }) => (
  <Link
    to={to}
    title={title}
    className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-navy transition-colors rounded-lg"
    data-testid={testId}
  >
    {label}
  </Link>
);

const NavDropdown = ({ label, testId, children }) => (
  <div className="relative group" data-testid={testId}>
    <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-600 hover:text-navy transition-colors rounded-lg">
      {label}
      <CaretDown className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />
    </button>
    <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
      <div className="bg-white border border-slate-200 rounded-xl shadow-xl p-2 min-w-[260px]">
        {children}
      </div>
    </div>
  </div>
);

const DropdownLink = ({ to, icon, label, desc }) => (
  <Link
    to={to}
    className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors group/item"
    data-testid={`nav-${to.replace(/\//g, "").replace(/-/g, "")}`}
  >
    <div className="mt-0.5 shrink-0">{icon}</div>
    <div>
      <p className="text-sm font-medium text-slate-900 group-hover/item:text-navy">{label}</p>
      <p className="text-xs text-slate-400">{desc}</p>
    </div>
  </Link>
);

const MobileLink = ({ to, label, onClick, className = "" }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`block px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 ${className}`}
  >
    {label}
  </Link>
);

export default Header;
